import { Plus, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { useCreateTag, useDeleteTag, useTags } from '../../common/admin-hooks.js';
import { useScenariosList } from '../../scenario/hooks.js';

/**
 * Planning · Governance. Two panels: tag administration + scenario
 * inventory. Tags are full-CRUD via the engine's `/v1/tags` route;
 * scenario list is read-only.
 *
 * Tag colours are chosen from a fixed palette tinted against the
 * dark theme — operators can't bring custom hex yet (the engine
 * accepts arbitrary strings but the chips look better on a curated
 * palette). Stage 2 wires the editing affordance + bulk-tag-scenarios
 * + a colour-wheel picker.
 */
const TAG_PALETTE: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Yellow', value: '#f8e767' },
  { label: 'Blue', value: '#0493fc' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Pink', value: '#ec4899' },
  { label: 'Violet', value: '#a855f7' },
  { label: 'Rose', value: '#ef4444' },
  { label: 'Cyan', value: '#06b6d4' },
];

export function PlanningGovernancePage(): JSX.Element {
  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Planning · Governance</h1>
        <p className="text-sm text-foreground/60">Scenario inventory + governance tags.</p>
      </header>
      <div className="grid grid-cols-1 gap-3 px-6 py-3 lg:grid-cols-2">
        <TagsPanel />
        <ScenariosSummary />
      </div>
    </div>
  );
}

function TagsPanel(): JSX.Element {
  const { data: tags = [], isLoading, error } = useTags();
  const { create, isPending: creating, error: createError } = useCreateTag();
  const { remove, isPending: deleting } = useDeleteTag();
  const [draft, setDraft] = useState('');
  const [pickedColor, setPickedColor] = useState<string>(TAG_PALETTE[0]!.value);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const title = draft.trim();
    if (!title) return;
    try {
      // Engine schema field is `title`, not `name`.
      await create({ title, color: pickedColor });
      setDraft('');
    } catch {
      /* error surfaces via createError below */
    }
  }

  return (
    <section className="rounded-md border border-foreground/10 bg-foreground/[0.02]">
      <header className="flex items-center gap-2 border-b border-foreground/10 px-4 py-2 text-sm font-medium text-foreground/80">
        <Tag className="h-4 w-4" /> Tags
        <span className="ml-auto text-xs text-foreground/40">{tags.length} defined</span>
      </header>
      <form onSubmit={submit} className="space-y-3 border-b border-foreground/10 p-3">
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="New tag name"
            className="grow border-foreground/15 bg-foreground/5 text-sm text-foreground placeholder:text-foreground/40"
          />
          <Button
            type="submit"
            disabled={creating || !draft.trim()}
            className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
          >
            {creating ? (
              'Adding…'
            ) : (
              <>
                <Plus className="mr-1 h-4 w-4" /> Add
              </>
            )}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground/50">Colour:</span>
          {TAG_PALETTE.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setPickedColor(c.value)}
              aria-label={`Pick ${c.label}`}
              title={c.label}
              className={`h-5 w-5 rounded-full border-2 transition-transform ${
                pickedColor === c.value ? 'scale-110 border-white' : 'border-foreground/20'
              }`}
              style={{ backgroundColor: c.value }}
            />
          ))}
        </div>
        {createError ? (
          <p className="text-xs text-rose-700 dark:text-rose-300">
            Could not add tag: {createError.message}
          </p>
        ) : null}
      </form>
      <div className="px-4 py-3">
        {isLoading ? (
          <p className="text-xs text-foreground/50">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-700 dark:text-rose-300">{error.message}</p>
        ) : tags.length === 0 ? (
          <p className="text-xs text-foreground/50">No tags yet — add one above.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const id =
                (t.id as string | number | undefined) ?? (t.tagId as string | number | undefined);
              const title =
                (t.title as string | undefined) ?? (t.name as string | undefined) ?? '(unnamed)';
              const color = (t.color as string | undefined) ?? null;
              return (
                <li
                  key={String(id ?? title)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-foreground/10 px-2 py-1 text-xs text-foreground/85"
                  style={{
                    backgroundColor: color ? `${color}33` : 'rgba(255,255,255,0.05)',
                  }}
                >
                  {color ? (
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                  ) : null}
                  {title}
                  {id !== undefined ? (
                    <button
                      type="button"
                      onClick={() => void remove(String(id))}
                      disabled={deleting}
                      className="text-foreground/40 transition-colors hover:text-rose-700 dark:hover:text-rose-300"
                      aria-label={`Delete tag ${title}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function ScenariosSummary(): JSX.Element {
  const { data, isLoading, error } = useScenariosList();
  return (
    <section className="rounded-md border border-foreground/10 bg-foreground/[0.02]">
      <header className="flex items-center gap-2 border-b border-foreground/10 px-4 py-2 text-sm font-medium text-foreground/80">
        Scenarios <span className="ml-auto text-xs text-foreground/40">{data?.length ?? 0}</span>
      </header>
      <div className="px-4 py-3 text-xs">
        {isLoading ? (
          <span className="text-foreground/50">Loading…</span>
        ) : error ? (
          <span className="text-rose-700 dark:text-rose-300">{error.message}</span>
        ) : !data || data.length === 0 ? (
          <span className="text-foreground/50">No scenarios</span>
        ) : (
          <ul className="space-y-1 text-sm">
            {data.slice(0, 15).map((row, i) => (
              <li key={i} className="truncate text-foreground/85">
                {(row.name as string | undefined) ?? `#${i}`}
              </li>
            ))}
            {data.length > 15 ? (
              <li className="text-foreground/50">… and {data.length - 15} more</li>
            ) : null}
          </ul>
        )}
      </div>
    </section>
  );
}
